import React, { useEffect, useRef, useState } from "react";
import { Button, Dropdown, Input, Modal, Segmented, message } from "antd";
import { KeyRound, MoreVertical, Network, PowerOff, RefreshCw, ScanSearch, ShieldCheck, Upload, UserPlus } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { FieldLabel, Note } from "../../../pages/enduser/ssh-proxy/sshProxyUi";
import {
  passwordErrorText,
  rerunAdminBootstrap,
  scanAccounts,
  setAdminCredential,
} from "../../../service/sshProxyService";
import { adminUsernameError } from "./adminCredential";
import { ChangeGatewayDialog } from "./CaTrustPanel";
import { PasswordSshDialog } from "./EndpointAccountsTab";
import { DecommissionDialog, passwordsKeptText, useScanStatus } from "./setupScan";

// The ⋮ menu in the endpoint details header, for administrators on a
// gateway endpoint: what the old Gateway Access tab offered, one dialog each.
//
// `adminStatus` is getAdminCredentialStatus's answer (or null); the page
// polls it while setup runs. onSetupQueued() after Update admin credential /
// Re-run setup, so the page shows setup as Running and polls. onScanFinished()
// after a scan queued here finishes. onGatewayChange(jumpServerId | null),
// onLifecycleChange(status) after those succeed. onAddUser() opens Add user.
// With `lifecycle` set (decommissioning / decommissioned) the menu is hidden.

const capitalise = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export const setupRunning = (st) => ["queued", "claimed"].includes(st?.bootstrap?.status);

const KEY_FILE_MAX = 32 * 1024;

// "Update admin credential…": the existing sudo account the gateway runs
// Authnull's setup through. The secret goes straight to the vault — it is
// cleared from state as soon as it is sent, and never stored in the browser.
export function AdminCredentialDialog({ open, instanceId, hostName, currentUsername, onClose, onSaved }) {
  const [username, setUsername] = useState("");
  const [method, setMethod] = useState("password"); // password | key
  const [secret, setSecret] = useState("");
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fileNote, setFileNote] = useState("");
  const fileInput = useRef(null);

  useEffect(() => {
    // Opening starts clean; closing (and unmount) drops any secret typed.
    setUsername(open ? currentUsername || "" : "");
    setMethod("password");
    setSecret("");
    setTouched(false);
    setSaving(false);
    setError("");
    setFileNote("");
  }, [open, currentUsername]);

  const name = username.trim();
  // The server's rule (adminCredential.js), including root needing a key.
  const nameError = adminUsernameError(name, method);
  const rootPassword = name === "root" && method === "password";
  const secretError = !secret
    ? method === "password"
      ? "Enter the password."
      : "Paste the private key or upload the key file."
    : method === "password" && /[\r\n]/.test(secret)
    ? "The password must be one line."
    : "";
  const formError = nameError || secretError;

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > KEY_FILE_MAX) {
      setFileNote("That file is too large to be a private key.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setSecret(String(reader.result || ""));
      setFileNote(`Loaded ${file.name}.`);
      setError("");
    };
    reader.onerror = () => setFileNote("Couldn't read that file.");
    reader.readAsText(file);
  };

  const submit = () => {
    setTouched(true);
    if (formError) return;
    const body = { instanceId: Number(instanceId), username: name };
    if (method === "password") body.password = secret;
    else body.privateKey = secret;
    setSaving(true);
    setError("");
    setAdminCredential({
      ...body,
      onSuccess: (res) => {
        setSaving(false);
        onSaved && onSaved(res || {});
      },
      onFailure: (err) => {
        setSaving(false);
        setError(passwordErrorText(err, "Couldn't save the admin credential."));
      },
    });
    // Sent: don't keep it around, even if the save fails.
    delete body.password;
    delete body.privateKey;
    setSecret("");
  };

  return (
    <Modal
      open={open}
      title={hostName ? `Update admin credential · ${hostName}` : "Update admin credential"}
      okText="Save and run setup"
      onOk={submit}
      confirmLoading={saving}
      okButtonProps={{ disabled: touched && !!formError }}
      onCancel={saving ? undefined : onClose}
      maskClosable={!saving}
      destroyOnClose
      width={560}
    >
      <div style={{ display: "grid", gap: 16, paddingTop: 4, fontFamily: AN_FONT }}>
        <div style={{ fontSize: 12.5, color: AN_COLORS.muted, lineHeight: 1.5 }}>
          An existing account with sudo on the machine. The gateway logs in as it to run Authnull's setup; the credential
          is kept in the vault and never shown again. Saving runs setup through it.
        </div>
        <div>
          <FieldLabel required>Username</FieldLabel>
          <Input
            value={username}
            placeholder="ubuntu"
            maxLength={32}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => {
              setUsername(e.target.value);
              setError("");
            }}
            status={touched && nameError ? "error" : undefined}
          />
          <div style={{ fontSize: 11.5, color: rootPassword ? AN_COLORS.danger : AN_COLORS.muted, marginTop: 4 }}>
            root can only be used with a private key.
          </div>
          {/* root + password is already said, in red, by the hint above. */}
          {touched && nameError && !rootPassword && <div style={{ fontSize: 11.5, color: AN_COLORS.danger, marginTop: 4 }}>{nameError}</div>}
        </div>
        <div>
          <FieldLabel required>Authenticate with</FieldLabel>
          <Segmented
            block
            value={method}
            onChange={(v) => {
              setMethod(v);
              setSecret("");
              setFileNote("");
              setError("");
            }}
            options={[
              { value: "password", label: "Password" },
              { value: "key", label: "Private key" },
            ]}
          />
          <div style={{ marginTop: 8 }}>
            {method === "password" ? (
              <Input.Password
                value={secret}
                placeholder="The account's password"
                autoComplete="new-password"
                onChange={(e) => {
                  setSecret(e.target.value);
                  setError("");
                }}
                status={touched && secretError ? "error" : undefined}
              />
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                <Input.TextArea
                  value={secret}
                  placeholder={"-----BEGIN OPENSSH PRIVATE KEY-----\n…"}
                  autoSize={{ minRows: 5, maxRows: 10 }}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => {
                    setSecret(e.target.value);
                    setFileNote("");
                    setError("");
                  }}
                  status={touched && secretError ? "error" : undefined}
                  style={{ fontFamily: AN_MONO, fontSize: 12 }}
                />
                <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                  <Button size="small" icon={<Upload size={13} style={{ verticalAlign: -2 }} />} onClick={() => fileInput.current?.click()}>
                    Upload key file
                  </Button>
                  {fileNote && <span style={{ fontSize: 11.5, color: AN_COLORS.muted }}>{fileNote}</span>}
                </span>
                <input ref={fileInput} type="file" style={{ display: "none" }} onChange={onFile} />
                <div style={{ fontSize: 11.5, color: AN_COLORS.muted }}>OpenSSH or PEM, without a passphrase.</div>
              </div>
            )}
          </div>
          {touched && secretError && <div style={{ fontSize: 11.5, color: AN_COLORS.danger, marginTop: 4 }}>{secretError}</div>}
        </div>
        {error && <Note tone="danger">{error}</Note>}
      </div>
    </Modal>
  );
}

export default function EndpointAdminMenu({
  endpoint,
  lifecycle,
  adminStatus,
  onAddUser,
  onSetupQueued,
  onScanFinished,
  onGatewayChange,
  onLifecycleChange,
}) {
  const [messageApi, msgCtx] = message.useMessage();
  const [modalApi, modalCtx] = Modal.useModal();
  const [dialog, setDialog] = useState(null); // gateway | credential | passwordSsh | decommission
  const { instanceId, hostName, jumpServerId } = endpoint || {};
  const scan = useScanStatus(instanceId, { onFinished: onScanFinished });
  const { watch } = scan;
  const running = setupRunning(adminStatus);

  const queueScan = () =>
    scanAccounts({
      instanceId,
      onSuccess: (res) => {
        messageApi.success("Scan queued — the users list refreshes when it finishes.");
        watch(res.jobId);
      },
      onFailure: (err) => {
        const status = err?.response?.status;
        messageApi.error(
          status === 409
            ? "Assign a gateway to this endpoint first"
            : status === 403
            ? "Administrator role required"
            : err?.response?.data?.error || "Couldn't queue the scan",
        );
      },
    });

  const confirmRerun = () =>
    modalApi.confirm({
      title: "Re-run setup on this endpoint?",
      content: `The gateway logs in as ${adminStatus?.username || "the admin account"} and runs Authnull's setup again with sudo — after an upgrade, or to repair the machine. Nothing changes for people using it.`,
      okText: "Re-run setup",
      cancelText: "Cancel",
      onOk: () =>
        new Promise((resolve) => {
          rerunAdminBootstrap({
            instanceId: Number(instanceId),
            onSuccess: () => {
              resolve();
              messageApi.success("Setup is running through the admin account.");
              onSetupQueued && onSetupQueued();
            },
            onFailure: (err) => {
              resolve();
              messageApi.error(passwordErrorText(err, "Couldn't re-run setup."));
            },
          });
        }),
    });

  const onDecommissioned = (res) => {
    if (res.status === "decommissioning") {
      const n = Number(res.grantsEnded) || 0;
      messageApi.info([`${n} grant${n === 1 ? "" : "s"} ended. The gateway is cleaning up the machine.`, passwordsKeptText(res)].filter(Boolean).join(" "));
      onLifecycleChange && onLifecycleChange("Decommissioning");
    } else if (res.status === "decommissioned") {
      if (res.machineCleaned !== false)
        messageApi.info([res.message ? capitalise(res.message) : "Decommissioned", passwordsKeptText(res)].filter(Boolean).join(". "));
      onLifecycleChange && onLifecycleChange("Decommissioned");
    }
  };

  const icon = (I) => <I size={13} />;
  const items = [
    { key: "add", icon: icon(UserPlus), label: "Add user…", onClick: onAddUser },
    { key: "scan", icon: icon(ScanSearch), label: scan.polling ? "Scanning accounts…" : "Scan accounts", disabled: scan.polling, onClick: queueScan },
    { type: "divider" },
    { key: "gateway", icon: icon(Network), label: "Change gateway…", onClick: () => setDialog("gateway") },
    { key: "credential", icon: icon(KeyRound), label: "Update admin credential…", onClick: () => setDialog("credential") },
    ...(adminStatus?.mode === "admin"
      ? [{ key: "rerun", icon: icon(RefreshCw), label: running ? "Setup running…" : "Re-run setup", disabled: running, onClick: confirmRerun }]
      : []),
    { key: "passwordSsh", icon: icon(ShieldCheck), label: "Password SSH exceptions…", onClick: () => setDialog("passwordSsh") },
    { type: "divider" },
    { key: "decommission", danger: true, icon: icon(PowerOff), label: "Decommission…", onClick: () => setDialog("decommission") },
  ];

  const close = () => setDialog(null);

  return (
    <>
      {msgCtx}
      {modalCtx}
      {/* A decommissioned endpoint has nothing left to change here; the
          dialogs stay mounted so Decommission can show its outcome. */}
      {!lifecycle && (
        <Dropdown trigger={["click"]} placement="bottomRight" menu={{ items }}>
          <Button aria-label="Endpoint actions" icon={<MoreVertical size={16} style={{ verticalAlign: -3 }} />} />
        </Dropdown>
      )}

      <ChangeGatewayDialog
        open={dialog === "gateway"}
        instanceId={instanceId}
        jumpServerId={jumpServerId || null}
        onClose={close}
        onSaved={(next) => {
          close();
          messageApi.success(next ? "Gateway saved" : "Gateway removed");
          onGatewayChange && onGatewayChange(next);
        }}
      />
      <AdminCredentialDialog
        open={dialog === "credential"}
        instanceId={instanceId}
        hostName={hostName}
        currentUsername={adminStatus?.mode === "admin" ? adminStatus.username : ""}
        onClose={close}
        onSaved={() => {
          close();
          messageApi.success("Admin credential saved — setup is running through it.");
          onSetupQueued && onSetupQueued();
        }}
      />
      <PasswordSshDialog
        open={dialog === "passwordSsh"}
        instanceId={instanceId}
        hostName={hostName}
        onRerunSetup={
          adminStatus?.mode === "admin" && !running
            ? () => {
                close();
                confirmRerun();
              }
            : undefined
        }
        onClose={close}
      />
      <DecommissionDialog
        open={dialog === "decommission"}
        instanceId={instanceId}
        hostName={hostName}
        hasGateway={!!jumpServerId}
        onClose={close}
        onDone={onDecommissioned}
      />
    </>
  );
}
